'use strict';

/**
 * Auth0 authentication provider for AngularJS.
 * Wraps angularAuth0 and provides a consistent API compatible with the authService facade.
 *
 * This provider delegates all authentication operations to Auth0's Angular SDK,
 * ensuring compatibility with the existing Auth0 integration.
 *
 * @module services.auth0Provider
 */
angular.module('services.auth0Provider', [])

.factory('auth0Provider', ['$q', '$location', '$window', '$injector', function($q, $location, $window, $injector) {
  var service = {};
  var angularAuth0 = null; // Will be lazily loaded when needed

  /**
   * Lazily retrieve angularAuth0 service.
   * Only loads when actually needed, preventing crashes when using Keycloak.
   *
   * @returns {object} angularAuth0 service instance
   */
  function getAngularAuth0() {
    if (!angularAuth0) {
      angularAuth0 = $injector.get('angularAuth0');
    }
    return angularAuth0;
  }

  /**
   * Initialize Auth0.
   * Auth0 SDK is already initialized via module config, so this is a no-op.
   * Provided for API consistency with keycloakProvider.
   *
   * @param {object} options - Optional initialization options (ignored for Auth0)
   * @returns {Promise<boolean>} Promise that resolves to true (Auth0 always ready)
   */
  service.init = function(options) {
    var deferred = $q.defer();

    // Auth0 SDK is initialized via angular-auth0-spa-js module config
    // No explicit initialization needed here
    deferred.resolve(true);

    return deferred.promise;
  };

  /**
   * Check if user is authenticated.
   *
   * @returns {Promise<boolean>} Promise that resolves to authentication status
   */
  service.isAuthenticated = function() {
    // getAngularAuth0().isAuthenticated() returns a promise
    return getAngularAuth0().isAuthenticated();
  };

  /**
   * Get current user profile.
   *
   * @returns {Promise<object|null>} Promise that resolves to user profile or null
   */
  service.user = function() {
    // getAngularAuth0().getUser() returns a promise
    return getAngularAuth0().getUser();
  };

  /**
   * Get fresh access token from Auth0.
   * Auth0 SDK handles token refresh automatically if needed.
   *
   * @returns {Promise<string>} Promise that resolves to access token
   */
  service.getAccessToken = function() {
    // getAngularAuth0().getTokenSilently() returns a promise
    return getAngularAuth0().getTokenSilently();
  };

  /**
   * Initiate Auth0 login flow.
   * Redirects user to Auth0's hosted login page.
   *
   * @param {string|null} appStateTarget - URL to return to after successful login (optional)
   */
  service.login = function(appStateTarget) {
    appStateTarget = appStateTarget || null;

    return getAngularAuth0().loginWithRedirect({
      appState: {
        target: appStateTarget || $location.url(),
      }
    });
  };

  /**
   * Log out from Auth0 and redirect.
   * Clears Auth0 session and local tokens.
   */
  service.logout = function() {
    return getAngularAuth0().logout({
      returnTo: window.location.origin
    });
  };

  /**
   * Handle Auth0 OAuth callback.
   * Processes the OAuth redirect callback and extracts the target route.
   * Only processes callback if OAuth parameters are present in URL.
   *
   * @returns {Promise<string|null>} Promise that resolves to target route or null
   */
  service.handleAuthentication = function() {
    // Check if this is an OAuth callback by looking for code and state parameters
    var search = $window.location.search;
    var hasCallbackParams = search.indexOf('code=') !== -1 && search.indexOf('state=') !== -1;

    if (!hasCallbackParams) {
      // Not a callback page - return immediately without processing
      var deferred = $q.defer();
      deferred.resolve(null);
      return deferred.promise;
    }

    // This IS a callback - process it
    return getAngularAuth0().handleRedirectCallback()
      .then(function(cbRes) {
        var targetRoute = cbRes.appState && cbRes.appState.target
          ? cbRes.appState.target
          : '/';

        return targetRoute;
      })
      .catch(function(err) {
        console.error('[auth0Provider] handleRedirectCallback error:', err);
        return null;
      });
  };

  return service;
}]);
